import React, { createContext, useContext, useState, useEffect } from 'react';
import { useAuth } from './AuthContext';

export type ViewMode = 'developer' | 'studio' | 'chatbot_generation' | 'chaining';

const VALID_VIEW_MODES: ViewMode[] = ['developer', 'studio', 'chatbot_generation', 'chaining'];

interface ViewModeContextType {
  viewMode: ViewMode;
  setViewMode: (mode: ViewMode) => void;
  intendedUse: string | null;
  setIntendedUse: (use: string) => void;
  hasCompletedOnboarding: boolean;
  completeOnboarding: () => Promise<void>;
}

const ViewModeContext = createContext<ViewModeContextType | undefined>(undefined);

export const useViewMode = () => {
  const context = useContext(ViewModeContext);
  if (context === undefined) {
    throw new Error('useViewMode must be used within a ViewModeProvider');
  }
  return context;
};

interface ViewModeProviderProps {
  children: React.ReactNode;
}

export const ViewModeProvider: React.FC<ViewModeProviderProps> = ({ children }) => {
  const { user } = useAuth();
  const [viewMode, setViewModeState] = useState<ViewMode>(() => {
    const savedMode = localStorage.getItem('viewMode');
    if (VALID_VIEW_MODES.includes(savedMode as ViewMode)) return savedMode as ViewMode;
    if (savedMode) localStorage.setItem('viewMode', 'developer');
    return 'developer';
  });
  const [intendedUse, setIntendedUseState] = useState<string | null>(null);
  const [hasCompletedOnboarding, setHasCompletedOnboarding] = useState(() => {
    return localStorage.getItem('hasCompletedOnboarding') === 'true';
  });

  // Load intended use from customer metadata
  useEffect(() => {
    const loadIntendedUse = async () => {
      if (!user) return;
      
      try {
        const token = await user.getIdToken();
        const response = await fetch('/api/v2/customers/current', {
          headers: { 'Authorization': `Bearer ${token}` }
        });
        
        if (response.ok) {
          const data = await response.json();
          const customerData = data.data;
          const config = customerData.config || {};
          
          if (config.intended_use) {
            setIntendedUseState(config.intended_use);
            
            // Set default view mode based on intended use if not already set
            const savedMode = localStorage.getItem('viewMode');
            if (!savedMode && config.intended_use) {
              const defaultMode = getDefaultViewMode(config.intended_use);
              setViewModeState(defaultMode);
              localStorage.setItem('viewMode', defaultMode);
            }
          }
          
          // Check if user has completed onboarding
          if (config.has_completed_onboarding) {
            setHasCompletedOnboarding(true);
            localStorage.setItem('hasCompletedOnboarding', 'true');
          }
        }
      } catch (error) {
        console.error('Failed to load intended use:', error);
      }
    };
    
    loadIntendedUse();
  }, [user]);

  const getDefaultViewMode = (use: string): ViewMode => {
    switch (use) {
      case 'developer':
      case 'api_integration':
        return 'developer';
      case 'chatbot_creator':
      case 'website_integration':
        return 'chatbot_generation';
      default:
        return 'developer';
    }
  };

  const setViewMode = (mode: ViewMode) => {
    setViewModeState(mode);
    localStorage.setItem('viewMode', mode);
  };

  const setIntendedUse = async (use: string) => {
    setIntendedUseState(use);
    
    // Update customer config
    if (user) {
      try {
        const token = await user.getIdToken();
        
        // Get current customer ID
        const currentResp = await fetch('/api/v2/customers/current', {
          headers: { 'Authorization': `Bearer ${token}` }
        });
        if (!currentResp.ok) return;
        
        const current = await currentResp.json();
        const customerId = current?.data?.customer_id;
        if (!customerId) return;
        
        // Update config
        await fetch(`/api/v2/customers/${customerId}`, {
          method: 'PATCH',
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          },
          body: JSON.stringify({
            config: {
              intended_use: use
            }
          })
        });
      } catch (error) {
        console.error('Failed to update intended use:', error);
      }
    }
  };

  const completeOnboarding = async () => {
    setHasCompletedOnboarding(true);
    localStorage.setItem('hasCompletedOnboarding', 'true');
    
    // Update customer config
    if (user) {
      try {
        const token = await user.getIdToken();
        
        // Get current customer ID
        const currentResp = await fetch('/api/v2/customers/current', {
          headers: { 'Authorization': `Bearer ${token}` }
        });
        if (!currentResp.ok) return;
        
        const current = await currentResp.json();
        const customerId = current?.data?.customer_id;
        if (!customerId) return;
        
        // Update config
        await fetch(`/api/v2/customers/${customerId}`, {
          method: 'PATCH',
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          },
          body: JSON.stringify({
            config: {
              has_completed_onboarding: true
            }
          })
        });
      } catch (error) {
        console.error('Failed to complete onboarding:', error);
      }
    }
  };

  const value = {
    viewMode,
    setViewMode,
    intendedUse,
    setIntendedUse,
    hasCompletedOnboarding,
    completeOnboarding
  };

  return <ViewModeContext.Provider value={value}>{children}</ViewModeContext.Provider>;
};

